import React, { useEffect, PropsWithChildren } from 'react';
import { css } from '@landr/styled-system/css';
import { Box, Container, useBreakpoint } from '@landr/maestro';
import { Group, getClonedChild, Skeleton, Flex } from '@landr/maestro';
import { useGradientContext } from 'controllers/GradientController/GradientController';
import Artwork from './components/Artwork';
import Title from './components/Title';
import Description from './components/Description';
import Byline from './components/Byline';
import Actions from './components/Actions';
import Attributes from './components/Attributes';

export const BylineWrapper: React.FC<
    PropsWithChildren<{ isMobile: boolean }>
> = ({ isMobile, children }) => (
    <Group
        direction={isMobile ? 'vertical' : 'horizontal'}
        gap={isMobile ? '2xs' : 'md'}
        mb={{
            base: 'xl',
            md: 'lg',
        }}
    >
        {children}
    </Group>
);

const getWrapperStyles = (hasArtwork: boolean) =>
    css({
        position: 'relative',
        paddingBottom: '2xl',
        textAlign: 'center',

        md: {
            textAlign: 'left',
            paddingBottom: hasArtwork ? '4xl' : '2xl',
        },
    });

interface SubComponents {
    Title: typeof Title;
    Description: typeof Description;
    Attributes: typeof Attributes;
    Byline: typeof Byline;
    Actions: typeof Actions;
    Artwork: typeof Artwork;
}

// custom page header that do not add same padding
const PageHeader: React.FC<
    PropsWithChildren<{
        backgroundColor?: string;
        foregroundColor?: string;
        isVerticalCentered?: boolean;
        isLoading?: boolean;
    }>
> &
    SubComponents = ({
    children,
    backgroundColor,
    foregroundColor,
    isVerticalCentered = false,
    isLoading = false,
}) => {
    const { updateGradient } = useGradientContext();

    useEffect(() => {
        if (backgroundColor !== undefined) {
            updateGradient(backgroundColor, foregroundColor);
        }
    }, [updateGradient, backgroundColor, foregroundColor]);

    const childrenArray = React.Children.toArray(children);

    const isMobile = useBreakpoint('md');

    const title = getClonedChild(childrenArray, PageHeader.Title, {
        isMobile: isMobile,
    });
    const description = getClonedChild(childrenArray, PageHeader.Description);
    const attributes = getClonedChild(childrenArray, PageHeader.Attributes, {
        isMobile: isMobile,
        isLoading,
    });
    const byline = getClonedChild(childrenArray, PageHeader.Byline, {
        isLoading,
    });
    const actions = getClonedChild(childrenArray, PageHeader.Actions, {
        isBottomAligned: !isMobile && !isVerticalCentered,
        isLoading,
    });

    const artwork = getClonedChild(childrenArray, PageHeader.Artwork, {
        isLoading,
    });

    return (
        <Box
            data-component="PageHeader"
            className={getWrapperStyles(!!artwork)}
        >
            <Container position="relative">
                <Group
                    gap={isMobile ? 'lg' : '2xl'}
                    direction={isMobile ? 'vertical' : 'horizontal'}
                >
                    {artwork && (
                        <Group.Item
                            position="relative"
                            margin={{ base: 'auto', md: '0 auto' }}
                        >
                            {artwork}
                        </Group.Item>
                    )}
                    <Group.Item
                        width={{
                            base: '100%',
                            md: `calc(100% - 220px - {spacing.2xl})`,
                        }}
                        display="flex"
                        flexDirection="column"
                        alignSelf={
                            !isMobile && isVerticalCentered
                                ? 'center'
                                : undefined
                        }
                        padding="none"
                    >
                        {title}
                        <BylineWrapper isMobile={isMobile}>
                            {isLoading ? (
                                <Flex flexDirection="row" mt="xs" width="100%">
                                    <Skeleton
                                        height="12px"
                                        width="10%"
                                        mr="xs"
                                    />
                                    <Skeleton height="12px" width="6%" />
                                </Flex>
                            ) : (
                                <>
                                    {byline}
                                    {attributes}
                                </>
                            )}
                        </BylineWrapper>
                        {!isMobile &&
                            (isLoading ? (
                                <Flex
                                    flexDirection="column"
                                    width="100%"
                                    mt="xl"
                                >
                                    <Skeleton
                                        height="20px"
                                        width="90%"
                                        mb="xs"
                                    />
                                    <Skeleton height="20px" width="90%" />
                                </Flex>
                            ) : (
                                <>{description}</>
                            ))}
                        {actions}
                        {isMobile && description && <Box>{description}</Box>}
                    </Group.Item>
                </Group>
            </Container>
        </Box>
    );
};

PageHeader.Title = Title;
PageHeader.Description = Description;
PageHeader.Attributes = Attributes;
PageHeader.Byline = Byline;
PageHeader.Actions = Actions;
PageHeader.Artwork = Artwork;

export default PageHeader;
